// AGC: Custom hook for plot timeline data and logic

import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from './useTranslation';
import { useLotTimeline } from './useLotTimeline'; // AGC: Use existing hook
import type {
  PlotTimelineData,
  PlotTimelineConfig,
  PlotTimelineEvent,
  MilestoneEvent,
  TaskEvent,
  NoteEvent,
  GrowthStage,
  TimelineSection,
} from '../types/plotTimeline';
import {
  getMilestoneIcon,
  getStageForDate,
  getDaysSincePlanting,
} from '../types/plotTimeline';
import { TASK_CATEGORIES } from '../types/task';

export interface UsePlotTimelineReturn {
  timelineData: PlotTimelineData | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

interface UsePlotTimelineProps {
  plotId: string;
  plotName: string;
  cropName: string;
  plantingDate: Date;
  harvestDate: Date;
  cropCycleLength?: number; // Days
}

export const usePlotTimeline = ({
  plotId,
  plotName,
  cropName,
  plantingDate,
  harvestDate,
  cropCycleLength = 120,
}: UsePlotTimelineProps): UsePlotTimelineReturn => {
  const { t } = useTranslation();
  const { tasks, notes, isLoading, error: timelineError } = useLotTimeline(plotId, 'all'); // AGC: Use existing hook

  const [error, setError] = useState<string | null>(timelineError);

  // AGC: Define growth stages for the crop (Soy/Corn example)
  const growthStages: GrowthStage[] = useMemo(() => [
    { code: 'VE', name: t('plotTimeline.stages.ve'), startDay: 0, icon: '🌱' },
    { code: 'V1', name: t('plotTimeline.stages.v1'), startDay: 7, icon: '🌿' },
    { code: 'V3', name: t('plotTimeline.stages.v3'), startDay: 21, icon: '🌿' },
    { code: 'V6', name: t('plotTimeline.stages.v6'), startDay: 42, icon: '🌿' },
    { code: 'R1', name: t('plotTimeline.stages.r1'), startDay: 60, icon: '🌺' },
    { code: 'R3', name: t('plotTimeline.stages.r3'), startDay: 75, icon: '🌸' },
    { code: 'R5', name: t('plotTimeline.stages.r5'), startDay: 90, icon: '🫘' },
    { code: 'R8', name: t('plotTimeline.stages.r8'), startDay: cropCycleLength - 10, icon: '🌾' },
  ], [cropCycleLength, t]);

  // AGC: Calculate timeline configuration
  const config: PlotTimelineConfig = useMemo(() => {
    const cycleStartDate = new Date(plantingDate);
    cycleStartDate.setDate(cycleStartDate.getDate() - 7); // Start 7 days before planting

    const cycleEndDate = new Date(harvestDate);

    const totalDays = Math.ceil(
      (cycleEndDate.getTime() - cycleStartDate.getTime()) / (24 * 60 * 60 * 1000)
    );

    const currentDay = Math.ceil(
      (new Date().getTime() - cycleStartDate.getTime()) / (24 * 60 * 60 * 1000)
    );

    const progressPercent = Math.min(100, Math.max(0, (currentDay / totalDays) * 100));

    return {
      plotId,
      plotName,
      cropName,
      plantingDate,
      harvestDate,
      cycleStartDate,
      cycleEndDate,
      totalDays,
      currentDay,
      progressPercent,
      stages: growthStages,
    };
  }, [plotId, plotName, cropName, plantingDate, harvestDate, growthStages]);

  // AGC: Build timeline events from tasks and notes
  const timelineData: PlotTimelineData | null = useMemo(() => {
    if (isLoading) return null;

    try {
      const events: PlotTimelineEvent[] = [];

      // AGC: Add planting milestone (check for duplicate task first)
      // AGC: Check if task with same title exists (Plantio/Siembra/Planting)
      const hasPlantingTask = tasks?.some((t) => {
        const lowerTitle = t.title.toLowerCase();
        return (
          (lowerTitle.includes('plantio') || 
           lowerTitle.includes('siembra') || 
           lowerTitle.includes('planting') ||
           lowerTitle.includes('semeadura')) &&
          Math.abs(new Date(t.dueDate).getTime() - plantingDate.getTime()) < 7 * 24 * 60 * 60 * 1000 // Within 7 days
        );
      });

      if (!hasPlantingTask) {
        const plantingMilestone: MilestoneEvent = {
          id: 'milestone-sowing',
          type: 'milestone',
          milestoneType: 'sowing',
          eventDate: plantingDate,
          stageCode: 'VE',
          title: t('plotTimeline.sowing'),
          description: plantingDate.toLocaleDateString('pt-BR'),
          icon: getMilestoneIcon('sowing'),
          isCompleted: plantingDate <= new Date(),
        };
        events.push(plantingMilestone);
      }

      // AGC: Add harvest milestone (check for duplicate task first)
      // AGC: Check if task with same title exists (Colheita/Cosecha/Harvest)
      const hasHarvestTask = tasks?.some((t) => {
        const lowerTitle = t.title.toLowerCase();
        return (
          (lowerTitle.includes('colheita') || 
           lowerTitle.includes('cosecha') || 
           lowerTitle.includes('harvest') ||
           lowerTitle.includes('coleta')) &&
          Math.abs(new Date(t.dueDate).getTime() - harvestDate.getTime()) < 14 * 24 * 60 * 60 * 1000 // Within 14 days
        );
      });

      if (!hasHarvestTask) {
        const harvestMilestone: MilestoneEvent = {
          id: 'milestone-harvest',
          type: 'milestone',
          milestoneType: 'harvest',
          eventDate: harvestDate,
          stageCode: 'R8',
          title: t('plotTimeline.harvest'),
          description: harvestDate.toLocaleDateString('pt-BR'),
          icon: getMilestoneIcon('harvest'),
          isCompleted: harvestDate <= new Date(),
        };
        events.push(harvestMilestone);
      }

      // AGC: Add tasks as timeline events
      if (tasks) {
        tasks.forEach((task) => {
          const taskDate = new Date(task.dueDate);
          const stageCode = getStageForDate(taskDate, plantingDate, growthStages);
          
          // AGC: Get icon from task category
          const categoryInfo = TASK_CATEGORIES.find(cat => cat.id === task.category);
          const taskIcon = categoryInfo?.icon || '📋';

          const taskEvent: TaskEvent = {
            id: `task-${task.id}`,
            type: 'task',
            taskId: task.id,
            eventDate: taskDate,
            dueDate: taskDate,
            stageCode,
            title: task.title,
            description: task.description,
            icon: taskIcon, // AGC: Add icon from category
            status: task.status as any,
            priority: task.priority as any,
            isCompleted: task.status === 'completed',
            completionDate: task.completedAt ? new Date(task.completedAt) : undefined,
          };
          events.push(taskEvent);
        });
      }

      // AGC: Add notes as timeline events
      if (notes) {
        notes.forEach((note) => {
          const noteDate = new Date(note.createdAt);
          const stageCode = getStageForDate(noteDate, plantingDate, growthStages);

          // AGC: Use note content as title; fall back to type label
          const noteTitle = note.content
            ? note.content.substring(0, 60) + (note.content.length > 60 ? '...' : '')
            : t('plotTimeline.note')

          const noteEvent: NoteEvent = {
            id: `note-${note.id}`,
            type: 'note',
            noteId: note.id,
            eventDate: noteDate,
            createdAt: noteDate,
            stageCode,
            title: noteTitle,
            noteType: note.type as any,
            content: note.content,
            // AGC: Map mediaUrl to the correct field based on note type
            audioUrl: note.type === 'audio' ? note.mediaUrl : undefined,
            photoUrl: (note.type === 'photo' || note.type === 'video') ? note.mediaUrl : undefined,
            createdBy: {
              id: note.userId,
              name: note.userName || t('common.user'),
              avatar: note.userAvatar,
            },
            isCompleted: true,
          };
          events.push(noteEvent);
        });
      }

      // AGC: Sort events chronologically
      events.sort((a, b) => a.eventDate.getTime() - b.eventDate.getTime());

      console.log(`📊 AGC: Timeline has ${events.length} total events`);

      // AGC: Calculate pixel positions (proportional to time)
      const totalHeight = 2000; // Total timeline height in pixels
      events.forEach((event, index) => {
        const t =
          (event.eventDate.getTime() - config.cycleStartDate.getTime()) /
          (config.cycleEndDate.getTime() - config.cycleStartDate.getTime());
        event.pixelPosition = Math.floor(t * totalHeight);
        event.offsetY = 0; // Will be calculated for stacking
        event.zIndex = 10;
        
        console.log(`📍 AGC: Event ${index + 1}/${events.length}: ${event.title} at position ${event.pixelPosition}px (date: ${event.eventDate.toLocaleDateString()})`);
      });

      // AGC: Detect and stack simultaneous events - FIX: Use better stacking logic
      const STACK_THRESHOLD = 80; // Minimum vertical spacing between events
      for (let i = 0; i < events.length; i++) {
        let stackOffset = 0;
        for (let j = i + 1; j < events.length; j++) {
          const distance = Math.abs(events[j].pixelPosition! - events[i].pixelPosition!);
          if (distance < STACK_THRESHOLD) {
            // Too close - stack it
            stackOffset += 100; // Stack each subsequent event 100px down
            events[j].offsetY = stackOffset;
            events[j].zIndex = 10 + (j - i);
            console.log(`🔄 AGC: Stacking event ${j + 1} with offset ${stackOffset}px`);
          } else {
            break; // No more close events
          }
        }
      }

      // AGC: Group events into sections (by stage)
      const sections: TimelineSection[] = [];
      let currentSection: TimelineSection | null = null;

      events.forEach((event) => {
        if (!currentSection || currentSection.stageCode !== event.stageCode) {
          if (currentSection) {
            sections.push(currentSection);
          }

          const stage = growthStages.find((s) => s.code === event.stageCode);
          currentSection = {
            id: `section-${event.stageCode}`,
            stageCode: event.stageCode,
            title: stage?.name || event.stageCode,
            startDate: event.eventDate,
            events: [event],
            isCurrentStage: false,
            height: 200, // Will be calculated
          };
        } else {
          currentSection.events.push(event);
        }
      });

      if (currentSection) {
        sections.push(currentSection);
      }

      // AGC: Calculate section heights
      sections.forEach((section) => {
        const minPos = Math.min(...section.events.map((e) => e.pixelPosition || 0));
        const maxPos = Math.max(
          ...section.events.map((e) => (e.pixelPosition || 0) + (e.offsetY || 0))
        );
        section.height = maxPos - minPos + 100; // Add padding
      });

      // AGC: Mark current stage
      const today = new Date();
      const currentStage = getStageForDate(today, plantingDate, growthStages);
      sections.forEach((section) => {
        section.isCurrentStage = section.stageCode === currentStage;
      });

      return {
        config,
        events,
        sections,
      };
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      return null;
    }
  }, [
    tasks,
    notes,
    isLoading,
    config,
    plantingDate,
    harvestDate,
    growthStages,
    t,
  ]);

  const refetch = () => {
    // AGC: Refetch is handled automatically by useLotTimeline's real-time listeners
  };

  return {
    timelineData,
    isLoading,
    error,
    refetch,
  };
};
